import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Icon } from '../../components/icons/Icon'
import { InlineAlert } from '../../components/InlineAlert'
import { LegalEntityLookupField } from '../branches/LegalEntityLookupField'
import { createProduction, createProductionFY, createProductionPhase } from '../../api/film'
import { listPrograms, type ProgramView } from '../../api/programs'
import { listFiscalYears, type FiscalYearView } from '../../api/fiscalYears'
import { ApiError } from '../../api/problem'

type EntityRef = { id: string; name: string }

const PRODUCTION_TYPES = ['feature', 'tv', 'commercial', 'game'] as const
const PHASES = ['development', 'preproduction', 'principal_photography', 'post'] as const

interface PhaseRow {
  phase: string
  location: string
}

const STEPS = ['Production', 'Phases', 'Fiscal years', 'Review & create'] as const

/**
 * IB-327 — the in-app "New Production" wizard: `/productions/new`
 * (docs/design/remaining-scope-designs.md §IB-327). Creates the Production shell, then its
 * optional Phase(s) and ProductionFY shell(s) (FR-M13.1), landing on `ProductionDetailScreen` —
 * where `SpendImportWizard` is already wired ("Import spend lines") for the spend-import half of
 * this story. No guarded field (`qualifyingPct`/in-state/credit figures) is written by this
 * screen or by spend import — those stay on the `AdjustmentService` path exclusively
 * (CLAUDE.md §2.1); `CreateProductionFYRequest` structurally carries none of the five guarded
 * figures (every created row is a bare shell).
 */
export function NewProductionScreen() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const initialLegalEntityId = searchParams.get('legalEntityId')

  const [step, setStep] = useState(0)
  const [legalEntity, setLegalEntity] = useState<EntityRef | null>(
    initialLegalEntityId ? { id: initialLegalEntityId, name: initialLegalEntityId } : null,
  )
  const [title, setTitle] = useState('')
  const [type, setType] = useState<(typeof PRODUCTION_TYPES)[number]>('feature')
  const [programs, setPrograms] = useState<ProgramView[]>([])
  const [baseStateProgramId, setBaseStateProgramId] = useState('')

  const [phaseRows, setPhaseRows] = useState<PhaseRow[]>([])

  const [fiscalYears, setFiscalYears] = useState<FiscalYearView[]>([])
  const [selectedFyIds, setSelectedFyIds] = useState<Set<string>>(new Set())

  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  useEffect(() => {
    listPrograms({ filter: 'kind==film', limit: 200 })
      .then((page) => setPrograms(page.items))
      .catch(() => setPrograms([]))
  }, [])

  useEffect(() => {
    if (step !== 2 || !legalEntity) return
    listFiscalYears('legal_entity', legalEntity.id)
      .then(setFiscalYears)
      .catch(() => setFiscalYears([]))
  }, [step, legalEntity])

  const canAdvanceFromStep0 = Boolean(legalEntity && title.trim() && baseStateProgramId)

  function addPhaseRow() {
    setPhaseRows((prev) => [...prev, { phase: 'development', location: '' }])
  }
  function updatePhaseRow(index: number, patch: Partial<PhaseRow>) {
    setPhaseRows((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)))
  }
  function removePhaseRow(index: number) {
    setPhaseRows((prev) => prev.filter((_, i) => i !== index))
  }

  function toggleFy(id: string) {
    setSelectedFyIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  async function handleCreate() {
    if (!legalEntity || !title.trim() || !baseStateProgramId || submitting) return
    setSubmitting(true)
    setSubmitError(null)
    try {
      const production = await createProduction({
        legalEntityId: legalEntity.id,
        title: title.trim(),
        type,
        baseStateProgramId,
      })
      for (const row of phaseRows) {
        if (!row.location.trim()) continue
        await createProductionPhase(production.id, { phase: row.phase, location: row.location.trim() })
      }
      for (const fyId of selectedFyIds) {
        await createProductionFY(production.id, { fiscalYearId: fyId })
      }
      navigate(`/productions/${production.id}`)
    } catch (err) {
      setSubmitError(err instanceof ApiError ? (err.problem.detail ?? err.message) : 'Could not create this production.')
      setSubmitting(false)
    }
  }

  return (
    <div style={{ padding: 'var(--iw-space-5)', maxWidth: 640, display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-4)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--iw-space-3)' }}>
        <span style={{ color: 'var(--iw-accent)' }}>
          <Icon name="film" size={20} />
        </span>
        <h1 style={{ margin: 0, fontSize: 'var(--iw-size-lg)' }}>New production</h1>
      </div>

      <ol
        aria-label="Wizard steps"
        style={{ display: 'flex', gap: 'var(--iw-space-3)', listStyle: 'none', margin: 0, padding: 0, fontSize: 'var(--iw-size-xs)' }}
      >
        {STEPS.map((label, index) => (
          <li
            key={label}
            aria-current={index === step ? 'step' : undefined}
            style={{
              color: index === step ? 'var(--iw-accent)' : 'var(--iw-text-muted)',
              fontWeight: index === step ? ('var(--iw-weight-medium)' as never) : undefined,
            }}
          >
            {index + 1}. {label}
          </li>
        ))}
      </ol>

      {submitError ? <InlineAlert severity="error">{submitError}</InlineAlert> : null}

      {step === 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-4)' }}>
          <LegalEntityLookupField id="new-production-legal-entity" label="Legal entity" value={legalEntity} onChange={setLegalEntity} />

          <div className="iw-field">
            <label className="iw-field-label" htmlFor="new-production-title">
              Title
            </label>
            <input id="new-production-title" className="iw-input" value={title} onChange={(e) => setTitle(e.target.value)} required />
          </div>

          <div className="iw-field">
            <label className="iw-field-label" htmlFor="new-production-type">
              Type
            </label>
            <select id="new-production-type" className="iw-input" value={type} onChange={(e) => setType(e.target.value as (typeof PRODUCTION_TYPES)[number])}>
              {PRODUCTION_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>

          <div className="iw-field">
            <label className="iw-field-label" htmlFor="new-production-base-state">
              Base state program
            </label>
            <select
              id="new-production-base-state"
              className="iw-input"
              value={baseStateProgramId}
              onChange={(e) => setBaseStateProgramId(e.target.value)}
            >
              <option value="">Select a program…</option>
              {programs.map((program) => (
                <option key={program.id} value={program.id}>
                  {program.jurisdiction} — {program.code}
                </option>
              ))}
            </select>
          </div>
        </div>
      ) : null}

      {step === 1 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-3)' }}>
          <p style={{ margin: 0, color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-sm)' }}>
            Optional — record the production's phases (development, pre-production, principal photography, post).
          </p>
          {phaseRows.map((row, index) => (
            <div key={index} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <select className="iw-input" value={row.phase} onChange={(e) => updatePhaseRow(index, { phase: e.target.value })} style={{ flex: 1 }}>
                {PHASES.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
              <input
                className="iw-input"
                placeholder="Location"
                value={row.location}
                onChange={(e) => updatePhaseRow(index, { location: e.target.value })}
                style={{ flex: 1 }}
              />
              <button type="button" className="iw-btn iw-btn--sm" onClick={() => removePhaseRow(index)}>
                Remove
              </button>
            </div>
          ))}
          <button type="button" className="iw-btn iw-btn--sm" style={{ alignSelf: 'flex-start' }} onClick={addPhaseRow}>
            <Icon name="plus" size={12} /> Add phase
          </button>
        </div>
      ) : null}

      {step === 2 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-3)' }}>
          <p style={{ margin: 0, color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-sm)' }}>
            Optional — attach one or more of this legal entity's fiscal years. Each creates a bare
            ProductionFY shell; the guarded QRE figures are assessed later, only through the
            adjustment path.
          </p>
          {fiscalYears.length === 0 ? (
            <p style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-sm)' }}>
              No fiscal years found for this legal entity yet — this step can be skipped and a
              ProductionFY added later from the production page.
            </p>
          ) : (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {fiscalYears.map((fy) => (
                <li key={fy.id}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input type="checkbox" checked={selectedFyIds.has(fy.id)} onChange={() => toggleFy(fy.id)} />
                    {fy.label} ({fy.startDate} – {fy.endDate})
                  </label>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}

      {step === 3 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-2)', fontSize: 'var(--iw-size-sm)' }}>
          <div>
            <strong>Legal entity:</strong> {legalEntity?.name}
          </div>
          <div>
            <strong>Title:</strong> {title}
          </div>
          <div>
            <strong>Type:</strong> {type}
          </div>
          <div>
            <strong>Base state program:</strong>{' '}
            {programs.find((p) => p.id === baseStateProgramId)?.code ?? baseStateProgramId}
          </div>
          <div>
            <strong>Phases:</strong> {phaseRows.filter((r) => r.location.trim()).length}
          </div>
          <div>
            <strong>Fiscal years:</strong> {selectedFyIds.size}
          </div>
        </div>
      ) : null}

      <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}>
        <div>
          {step > 0 ? (
            <button type="button" className="iw-btn" onClick={() => setStep((s) => s - 1)}>
              Back
            </button>
          ) : (
            <Link to="/cases?kind=film" className="iw-btn">
              Cancel
            </Link>
          )}
        </div>
        <div>
          {step < STEPS.length - 1 ? (
            <button type="button" className="iw-btn iw-btn--primary" disabled={step === 0 && !canAdvanceFromStep0} onClick={() => setStep((s) => s + 1)}>
              Next
            </button>
          ) : (
            <button type="button" className="iw-btn iw-btn--primary" disabled={!canAdvanceFromStep0 || submitting} onClick={() => void handleCreate()}>
              {submitting ? 'Creating…' : 'Create production'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
